/**
 * The one shortcut registry (BUILD-PLAN 3.6): it binds keys, feeds the `?`
 * sheet and supplies the hints shown in the palette and tooltips.
 *
 * Shortcuts registered by a mounted component live as long as it does, so a
 * screen's single-key verbs (`T` today on the schedule) disappear when the
 * user leaves that screen. Later registrations of the same keys win.
 */
import { useEffect, useRef } from 'react';
import { createRegistry } from '../registry/store.js';
import { parseShortcut, type ParsedShortcut } from './keys.js';

export interface ShellShortcut {
  /** Unique id; defaults to the section plus keys when registered by hook. */
  id: string;
  /** Manifest notation: `g s`, `shift+p`, `mod+k`, `?`. */
  keys: string;
  description: string;
  /** Sheet section, e.g. "Global", "Navigation", "Schedule". */
  section: string;
  /** Fire while typing in a field (chords with Ctrl/Cmd/Alt always may). */
  allowInInputs?: boolean;
  /** Listed in the sheet but bound elsewhere (e.g. by a focused grid). */
  displayOnly?: boolean;
  run?: () => void;
}

export interface RegisteredShortcut extends ShellShortcut {
  parsed: ParsedShortcut;
}

export const shortcutRegistry = createRegistry<RegisteredShortcut>();

export function registerShortcut(shortcut: ShellShortcut): () => void {
  return shortcutRegistry.register({ ...shortcut, parsed: parseShortcut(shortcut.keys) });
}

export interface UseShortcutOptions {
  section?: string;
  allowInInputs?: boolean;
  enabled?: boolean;
  id?: string;
}

/** Binds `keys` to `handler` while the component is mounted. */
export function useShortcut(
  keys: string,
  description: string,
  handler: () => void,
  options: UseShortcutOptions = {},
): void {
  const ref = useRef(handler);
  ref.current = handler;
  const { section = 'This screen', allowInInputs = false, enabled = true, id } = options;
  useEffect(() => {
    if (!enabled) return undefined;
    return registerShortcut({
      id: id ?? `${section}:${keys}`,
      keys,
      description,
      section,
      allowInInputs,
      run: () => ref.current(),
    });
  }, [keys, description, section, allowInInputs, enabled, id]);
}

/** Sections in display order; unknown sections follow alphabetically. */
export const SECTION_ORDER = ['Global', 'Navigation', 'Offices', 'This screen'];

export function sortSections(names: readonly string[]): string[] {
  return [...new Set(names)].sort((a, b) => {
    const ia = SECTION_ORDER.indexOf(a);
    const ib = SECTION_ORDER.indexOf(b);
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    return a.localeCompare(b);
  });
}
